import microuniversoApi from "app/api/microuniversoApi";
import { Breadcrumb2Container } from "app/components/breadcrumb2/Breadcrumb2Container";
import { useAppSelector } from "app/redux/hooks";
import { IColuna } from "app/types/IColuna";
import { obterPermissao } from "app/use-cases/obterPermissao";
import { tratamentoErroPadrao } from "app/use-cases/tratamentoErroPadrao";
import { Dict } from "app/utils/Dict";
import globalMU from "globalMU";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { substituirItemEmArray } from "substituirItemEmArray";
import { DocumentosQuitados } from "./DocumentoQuitado";
import { obterParametros, obterParametrosPorOidItem } from "app/use-cases/parametro/obterParametros";

export type DocumentoQuitadoContainerProps = {
    codigoCliente: number
    onVoltar: () => void;
    passos: Dict<string, () => void>;
    onRemover: () => boolean
}

const parametroFonteReceita = "UTILIZA_RELACIONAMENTO_FILIAL_VENDEDOR_FONTE_RECEITA";
const nomeGrid: string = "DOCUMENTOS_QUITADOS_CLIENTE";
const tamanhoPagina: number = 20;
// referencia estavel: `colunas || []` criaria um array novo a cada render e anularia o React.memo
const colunasVazias: IColuna[] = [];

export const DocumentoQuitadoContainer = (props: DocumentoQuitadoContainerProps) => {
    const orcamento = useAppSelector((store) => store.orcamento);
    const [valorTotalDocumentosQuitados, setValorTotalDocumentosQuitados] = useState<number>(0);
    const [documentosQuitados, setDocumentosQuitados] = useState<any[]>()
    const [numeroDePaginas, setNumeroDePaginas] = useState<number>(1);
    const [paginaAtual, setPaginaAtual] = useState<number>(1);
    const [permiteEngrenagemGrid, setPermiteEngrenagemGrid] = useState<boolean>(false)
    const [colunaOrdenacao, setColunaOrdenacao] = useState<string>("")
    const [ordemOrdenacaoDecrescente, setOrdemOrdenacaoDecrescente] = useState<boolean>(false)
    const [paramUTILIZA_RELACIONAMENTO_FILIAL_VENDEDOR_FONTE_RECEITA, setParamUTILIZA_RELACIONAMENTO_FILIAL_VENDEDOR_FONTE_RECEITA] = useState<boolean>(false)

    const [colunas, setColunas] = useState<IColuna[]>();
    const [tamanhoFonte, setTamanhoFonte] = useState<number>(0);

    const passosInterno = useRef<Dict<string, () => void>>(new Dict(Object.assign({}, props.passos.map)))
    const isMounted = useRef<boolean>(true);

    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false };
    }, []);

    const atualizarParametros = useCallback(async () => {
        let oidFilial = orcamento?.OidFilialOrcamento ?? 7;

        try {
            const param = oidFilial > 7
                ? await obterParametrosPorOidItem({ nomes: [parametroFonteReceita], oidItem: oidFilial })
                : await obterParametros({ nomes: [parametroFonteReceita] }); // se chamado fora do orçamento

            if (isMounted.current) {
                setParamUTILIZA_RELACIONAMENTO_FILIAL_VENDEDOR_FONTE_RECEITA(param.find((p) => p.Nome == parametroFonteReceita)?.Valor == "S") // 487678/449093
            }
        }
        catch (e) {
            tratamentoErroPadrao(e)
        }
    }, [orcamento?.OidFilialOrcamento])

    const listarDocumentosQuitados = useCallback(async (numeroPagina?: number, coluna?: string, decrescente?: boolean) => {
        try {
            globalMU.blockUI();
            const resultado = await microuniversoApi.Cliente.ListarDocumentosQuitados(props.codigoCliente, tamanhoPagina, numeroPagina, coluna, decrescente);
            if (isMounted.current && !("HasErrors" in resultado)) {
                setColunaOrdenacao(coluna ?? "")
                setOrdemOrdenacaoDecrescente(decrescente)
                setDocumentosQuitados(resultado?.Entities ?? []);
                setNumeroDePaginas(resultado.PageCount);
                setPaginaAtual(resultado.ActualPage);
            }
        } catch (e) {
            tratamentoErroPadrao(e)
        } finally {
            globalMU.unblockUI();
        }
    }, [props.codigoCliente]);

    const totalDocumentosQuitados = useCallback(async () => {
        try {
            globalMU.blockUI();

            const resultado = await microuniversoApi.Cliente.ObterValorTotalDocumentosQuitados(props.codigoCliente);
            if (isMounted.current) {
                setValorTotalDocumentosQuitados(resultado)
            }
        } catch (e) {
            tratamentoErroPadrao(e)
        } finally {
            globalMU.unblockUI();
        }
    }, [props.codigoCliente]);

    const onAlterarFonte = useCallback(async (tamanho: number) => {
        globalMU.blockUI()
        try {
            await microuniversoApi.Usuario.alterarFonte(nomeGrid, tamanho)
            if (isMounted.current) {
                setTamanhoFonte(tamanho);
            }
        } catch (e) {
            tratamentoErroPadrao(e)
        }
        finally {
            globalMU.unblockUI()
        }
    }, []);

    const onToggleColunaSelecionada = useCallback(async (nomeColuna: string, selecionada: boolean) => {
        let coluna = { ...colunas.find((c) => c.Nome == nomeColuna) };
        coluna.Selecionada = selecionada;

        const colunas_ = substituirItemEmArray(colunas, (c) => c.Nome == nomeColuna, coluna);
        setColunas(colunas_);

        await microuniversoApi.Seguranca.atualizarColunasGrid(colunas_, nomeGrid);
    }, [colunas]);

    const onToggleSelecionarTodasColunas = useCallback(async (selecionar: boolean) => {
        const colunas_ = colunas.map(c => ({ ...c, Selecionada: selecionar } as IColuna))
        setColunas(colunas_)

        await microuniversoApi.Seguranca.atualizarGrid({
            CodigoUsuario: globalMU.userIdForGrid(),
            Modulo: "Orcamento",
            Token: globalMU.token(),
            NomeGrid: nomeGrid,
            Colunas: colunas_
        })
    }, [colunas]);

    const onObterDadosGridDocQuitados = useCallback(async () => {
        try {
            globalMU.blockUI();
            // requisicoes independentes: em paralelo em vez de encadeadas
            const [permissao, resposta] = await Promise.all([
                obterPermissao({ funcionalidade: "ORCAMENTO_ACESSO_ENGRENAGEM_GRID", acao: null }),
                microuniversoApi.Seguranca.obterDadosGrid("Orcamento", nomeGrid),
            ]);

            if (isMounted.current) {
                setPermiteEngrenagemGrid(permissao);
                if (resposta != null) {
                    setColunas(resposta.Colunas);
                    setTamanhoFonte(resposta.TamanhoFonte <= 2 ? 2 : resposta.TamanhoFonte);
                }
            }
        } catch (e) {
            tratamentoErroPadrao(e)
        } finally {
            globalMU.unblockUI();
        }
    }, []);

    const onPassoSelecionado = useCallback((passo: string) => {
        props.onRemover()
        passosInterno.current.get(passo)()
    }, [props.onRemover]);

    const onVoltar = useCallback(() => {
        props.onRemover()
        props.onVoltar()
    }, [props.onRemover, props.onVoltar]);

    const onAlterarListaSelecionada = useCallback((numeroPagina: number) => listarDocumentosQuitados(numeroPagina), [listarDocumentosQuitados]);

    const onAlterarOrdenacaoDados = useCallback((coluna: string, decrescente: boolean) => listarDocumentosQuitados(1, coluna, decrescente), [listarDocumentosQuitados]);

    useEffect(() => {
        atualizarParametros() // 487678/449093
        passosInterno.current.set("Documentos Quitados", () => { })
        onObterDadosGridDocQuitados()
        listarDocumentosQuitados()
        totalDocumentosQuitados()
    }, [])

    return (
        <div id="DocumentoQuitadoContainer">
            <Breadcrumb2Container
                passos={passosInterno.current.keysToList()}
                exibirBotaoLimpar={false}
                exibirBotaoVoltar={true}
                id="DocumentosQuitadosContainer"
                onPassoSelecionado={onPassoSelecionado}
                onBotaoVoltarClicado={onVoltar}
            />

            <DocumentosQuitados
                colunaOrdenacao={colunaOrdenacao}
                entidades={documentosQuitados}
                valorTotalDocumentosQuitados={valorTotalDocumentosQuitados}
                cpfCnpj={orcamento.Cliente.CpfCnpj}
                nome={orcamento.Cliente.Nome}
                paginaAtual={paginaAtual}
                numeroTotalPaginas={numeroDePaginas}
                tamanhoPagina={tamanhoPagina}
                onAlterarListaSelecionada={onAlterarListaSelecionada}
                colunas={colunas ?? colunasVazias}
                onAlterarFonte={onAlterarFonte}
                onToggleColunaSelecionada={onToggleColunaSelecionada}
                onToggleSelecionarTodasColunas={onToggleSelecionarTodasColunas}
                tamanhoFonte={tamanhoFonte}
                permiteEngrenagemGrid={permiteEngrenagemGrid}
                nomeGrid={nomeGrid}
                ordemOrdenacaoDecrescente={ordemOrdenacaoDecrescente}
                onAlterarOrdenacaoDados={onAlterarOrdenacaoDados}
                usaFonteDeReceita={paramUTILIZA_RELACIONAMENTO_FILIAL_VENDEDOR_FONTE_RECEITA} // 487678/449093
            />
        </div>
    )
}
